/**
 * Web-push subscription helpers for the FuelExpress PWA.
 *
 * The browser flow is:
 *   1. Fetch the tenant's VAPID public key from the backend.
 *   2. `Notification.requestPermission()` — must be inside a user gesture.
 *   3. `registration.pushManager.subscribe(...)` with that key.
 *   4. `POST /api/push/subscribe` with the subscription JSON so the server
 *      can later dispatch pushes via `send_push(user_id, ...)`.
 *
 * All functions are no-ops when the browser doesn't support push (older iOS
 * Safari, in-app webviews) or when we're not running under a service worker
 * (development builds).
 */
import api from "@/api/client";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

/** VAPID keys are base64url; the browser needs a Uint8Array. */
function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Enable push for the caller. Returns the resulting `PushSubscription`
 * (already POSTed to the backend) or throws with a caller-friendly message.
 */
export async function enablePush() {
  if (!isPushSupported()) throw new Error("Push notifications aren't supported in this browser.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied. Enable notifications in your browser settings.");

  const reg = await navigator.serviceWorker.ready;

  // Reuse any existing subscription so we don't accidentally rotate keys.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/vapid-public-key");
    if (!data?.public_key) throw new Error("Push isn't configured on this deployment yet.");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }

  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function disablePush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try { await api.post("/push/unsubscribe", sub.toJSON()); } catch { /* ignore */ }
  await sub.unsubscribe();
}

/** Convenience — ask the backend to send a test push to the caller. */
export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
